import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { base_url } from "../../utils/api";
import { getUser } from "../../utils/constants";
import Processing from "../../components/Processing/Processing";
import { Pagination, Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  styled as muiStyled,
  IconButton,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";

const UploadArea = muiStyled(Box)(({ theme, isDragging }) => ({
  border: `2px dashed ${
    isDragging ? theme.palette.primary.main : theme.palette.grey[500]
  }`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  textAlign: "center",
  backgroundColor: isDragging
    ? theme.palette.action.hover
    : theme.palette.background.paper,
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: theme.palette.primary.main,
  },
}));

const StyledInput = muiStyled("input")({
  display: "none",
});

const Banners = () => {
  const [loading, setLoading] = useState(false);
  const [banners, setBanners] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [toDeleteBannerId, setToDeleteBannerId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const user = JSON.parse(getUser() || "{}");

  useEffect(() => {
    getBanners(1);
  }, []);

  useEffect(() => {
    return () => {
      if (
        imagePreview &&
        typeof imagePreview === "string" &&
        imagePreview.startsWith("blob:")
      ) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const getBanners = async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get(`${base_url}banner/getAllBanners`, {
        params: { page, pageSize: 10 },
        headers: {
          Authorization: `Bearer ${user?.authToken}`,
          Accept: "application/json",
        },
      });

      setBanners(response?.data?.data || []);
      setTotalPages(response?.data?.totalPages || 1);
      setCurrentPage(response?.data?.currentPage || 1);
    } catch (error) {
      console.error("getBanners error", error);
      const message =
        error.response?.data?.message ||
        "Network error, please try again later";
      toast.error(message, {
        position: toast.POSITION.TOP_RIGHT,
        className: "bg-danger text-white",
      });
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setIsEdit(false);
    setSelectedBanner(null);
    setImagePreview(null);
    setShowModal(true);
    formik.resetForm();
  };

  const openEditModal = (banner) => {
    setIsEdit(true);
    setSelectedBanner(banner);
    setImagePreview(banner?.banner_image || null);
    setShowModal(true);

    formik.setValues({
      link: banner?.banner_link || "",
      image: null,
    });
    formik.setFieldTouched("image", false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0] || null;
    if (file) {
      handleFileChange(file);
    }
  };

  const handleFileChange = (file) => {
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a valid image (JPEG, JPG, or PNG)", {
        position: toast.POSITION.TOP_RIGHT,
        className: "bg-danger text-white",
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
      return;
    }

    // const maxSize = 5 * 1024 * 1024;
    // if (file.size > maxSize) {
    //   toast.error("Image size must be less than 5MB", {
    //     position: toast.POSITION.TOP_RIGHT,
    //     className: "bg-danger text-white",
    //   });
    //   if (fileInputRef.current) {
    //     fileInputRef.current.value = null;
    //   }
    //   return;
    // }

    if (imagePreview && imagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(URL.createObjectURL(file));
    formik.setFieldValue("image", file);
    setTimeout(() => {
      formik.setFieldTouched("image", true, true);
    }, 0);
  };

  const handleRemoveImage = () => {
    if (imagePreview && imagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
    formik.setFieldValue("image", null);
    formik.setFieldTouched("image", true, true);
  };

  const deleteBanner = async () => {
    setLoading(true);
    try {
      await axios.delete(`${base_url}banner/deleteBanner`, {
        params: { banner_id: toDeleteBannerId },
        headers: { Authorization: `Bearer ${user?.authToken}` },
      });
      toast.success("Banner deleted successfully ✅", {
        position: toast.POSITION.TOP_RIGHT,
        className: "bg-success text-white",
      });
      setShowDeleteModal(false);
      setToDeleteBannerId(null);
      setCurrentPage(1);
      getBanners(1);
    } catch (error) {
      console.error("deleteBanner error", error);
      const message =
        error.response?.data?.message ||
        "Network error, please try again later";
      toast.error(message, {
        position: toast.POSITION.TOP_RIGHT,
        className: "bg-danger text-white",
      });
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: { image: null, link: "" },
    validate: (values) => {
      const errors = {};

      // Image validation
      if (!isEdit) {
        if (!values.image) {
          errors.image = "Image is required";
        }
      } else {
        if (!values.image && !imagePreview) {
          errors.image = "Image is required";
        }
      }

      // Link validation (only if provided)
      if (values.link && values.link.trim() !== "") {
        try {
          Yup.string()
            .url("Must be a valid URL")
            .validateSync(values.link.trim());
        } catch (error) {
          errors.link = error.message;
        }
      }

      return errors;
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        if (isEdit && selectedBanner?.banner_id) {
          if (values.image instanceof File) {
            // Multipart for update with new image
            const formData = new FormData();
            formData.append("banner_image", values.image);
            formData.append("banner_link", values.link || "");
            await axios.put(`${base_url}banner/updateBanner`, formData, {
              params: { banner_id: selectedBanner.banner_id },
              headers: {
                Authorization: `Bearer ${user?.authToken}`,
                "Content-Type": "multipart/form-data",
              },
            });
          } else {
            // JSON for update without new image (keep existing image)
            await axios.put(
              `${base_url}banner/updateBanner`,
              { banner_link: values.link || "" },
              {
                params: { banner_id: selectedBanner.banner_id },
                headers: {
                  Authorization: `Bearer ${user?.authToken}`,
                  "Content-Type": "application/json",
                },
              }
            );
          }
          toast.success("Banner updated successfully ✅", {
            position: toast.POSITION.TOP_RIGHT,
            className: "bg-success text-white",
          });
        } else {
          // Create new banner (requires image)
          if (!values.image) {
            toast.error("Image is required for adding a new banner", {
              position: toast.POSITION.TOP_RIGHT,
              className: "bg-danger text-white",
            });
            return;
          }
          const formData = new FormData();
          formData.append("banner_image", values.image);
          formData.append("banner_link", values.link || "");
          await axios.post(`${base_url}banner/createBanner`, formData, {
            headers: {
              Authorization: `Bearer ${user?.authToken}`,
              "Content-Type": "multipart/form-data",
            },
          });
          toast.success("Banner added successfully ✅", {
            position: toast.POSITION.TOP_RIGHT,
            className: "bg-success text-white",
          });
        }
        setShowModal(false);
        setCurrentPage(1);
        getBanners(1);
      } catch (error) {
        console.error("add/update banner error", error);
        const message =
          error.response?.data?.message ||
          "Network error, please try again later";
        toast.error(message, {
          position: toast.POSITION.TOP_RIGHT,
          className: "bg-danger text-white",
        });
      } finally {
        setLoading(false);
      }
    },
  });

  const handleStatusChange = async (banner, newStatus) => {
    setLoading(true);
    try {
      await axios.put(
        `${base_url}banner/updateBannerStatus`,
        {},
        {
          params: {
            banner_id: banner?.banner_id,
            is_active: newStatus,
          },
          headers: {
            Authorization: `Bearer ${user?.authToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      toast.success(`Banner ${newStatus ? "activated" : "deactivated"} ✅`, {
        position: toast.POSITION.TOP_RIGHT,
        className: "bg-success text-white",
      });
      setBanners((prev) =>
        prev.map((b) =>
          b.banner_id === banner.banner_id ? { ...b, is_active: newStatus } : b
        )
      );
    } catch (error) {
      console.error("Status change error", error);
      const message =
        error.response?.data?.message ||
        "Network error, please try again later";
      toast.error(message, {
        position: toast.POSITION.TOP_RIGHT,
        className: "bg-danger text-white",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
    getBanners(pageNumber);
  };

  const handlePrevious = () => {
    if (currentPage !== 1) {
      const next = currentPage - 1;
      setCurrentPage(next);
      getBanners(next);
    }
  };

  const handleNext = () => {
    if (currentPage !== totalPages) {
      const next = currentPage + 1;
      setCurrentPage(next);
      getBanners(next);
    }
  };

  const getPaginationItems = () => {
    const items = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || Math.abs(i - currentPage) <= 2) {
        items.push(
          <Pagination.Item
            key={i}
            onClick={() => handlePageClick(i)}
            active={i === currentPage}
            role="button"
          >
            {i}
          </Pagination.Item>
        );
      } else if (items[items.length - 1] !== "...") {
        items.push("...");
      }
    }
    return items;
  };

  const WarningSwitch = styled(Switch)(({ theme }) => ({
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: "#FFC107",
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#d3b251ff",
    },
    "& .MuiSwitch-track": {
      backgroundColor: "#25241fff",
    },
  }));

  return (
    <div id="main" className="main">
      {loading && <Processing />}

      <div className="pagetitle">
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Dashboard</Link>
            </li>
            <li className="breadcrumb-item active">Banners</li>
          </ol>
        </nav>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-lg-between btn-View">
            <div>
              <h3 className="m-0 font-weight-bold">Banners List</h3>
            </div>
            {user?.type === "super_admin" && (
              <div className=" text-center ">
                <button className="btn btn-warning" onClick={openAddModal}>
                  Add Banner
                </button>
              </div>
            )}
          </div>

          <hr />

          <div className="table-responsive">
            <table className="table text-nowrap align-items-end table-hover">
              <thead>
                <tr>
                  <th scope="col">Id</th>
                  <th scope="col">Image</th>
                  <th scope="col">Link</th>
                  <th scope="col" className="text-center">
                    Status
                  </th>
                  <th scope="col" className="pe-4 text-end">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {banners?.map((item, index) => {
                  const imageUrl = item?.banner_image || null;

                  return (
                    <tr key={item?.banner_id} className="align-middle">
                      <td>{(currentPage - 1) * 10 + index + 1}</td>
                      <td>
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt="banner"
                            style={{
                              width: 50,
                              height: 50,
                              borderRadius: "8px",
                              objectFit: "cover",
                              cursor: "pointer",
                            }}
                            onClick={() => window.open(imageUrl, "_blank")}
                          />
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td
                        style={{
                          maxWidth: "200px",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {item?.banner_link ? (
                          <a
                            href={item?.banner_link}
                            target="_blank"
                            rel="noreferrer"
                            className="d-block"
                            style={{ wordBreak: "break-word" }}
                          >
                            {item?.banner_link}
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td className="text-center">
                        <FormControlLabel
                          control={
                            <WarningSwitch
                              checked={item?.is_active}
                              onChange={(e) =>
                                handleStatusChange(item, e.target.checked)
                              }
                              name={`statusSwitch-${item?.banner_id}`}
                              inputProps={{
                                "aria-label": `status switch ${item?.banner_id}`,
                              }}
                              sx={{ marginLeft: 2 }}
                            />
                          }
                          label=""
                        />
                      </td>
                      <td className="text-center">
                        <div className="d-flex justify-content-end align-items-center gap-2 me-3">
                          <button
                            type="button"
                            className="btn p-0"
                            onClick={() => openEditModal(item)}
                            aria-label={`Edit banner ${item.banner_id}`}
                          >
                            <i className="bi bi-pencil-square"></i>
                          </button>
                          {user?.type === "super_admin" && (
                            <button
                              type="button"
                              className="btn p-0"
                              onClick={() => {
                                setToDeleteBannerId(item?.banner_id);
                                setShowDeleteModal(true);
                              }}
                              aria-label={`Delete banner ${item.banner_id}`}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <Pagination className="align-items-center justify-content-center">
              <Pagination.Prev
                onClick={handlePrevious}
                disabled={currentPage === 1}
                aria-label="Previous page"
              >
                Previous
              </Pagination.Prev>
              {getPaginationItems()}
              <Pagination.Next
                onClick={handleNext}
                disabled={currentPage === totalPages || banners.length === 0}
                aria-label="Next page"
              >
                Next
              </Pagination.Next>
            </Pagination>
          )}

          {totalPages <= 1 && banners?.length === 0 && (
            <p className="text-center">No data available</p>
          )}
        </div>
      </div>

      <Modal show={showModal} centered onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{isEdit ? "Edit Banner" : "Add Banner"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={formik.handleSubmit}>
            <label className="form-label">
              Banner Image<span className="text-danger">*</span>
            </label>
            <Box>
              <UploadArea
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                isDragging={isDragging}
              >
                <label htmlFor="image">
                  <StyledInput
                    id="image"
                    name="image"
                    type="file"
                    accept="image/jpeg,image/png"
                    ref={fileInputRef}
                    onChange={(event) => {
                      const file = event.currentTarget.files?.[0] || null;
                      handleFileChange(file);
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <CloudUploadIcon
                      color="primary"
                      sx={{ fontSize: 40, mb: 1 }}
                    />
                    <Typography variant="body1">
                      {isDragging
                        ? "Drop the file here"
                        : "Click or drag file to upload"}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {/* (Max size: 5MB, Allowed: JPG, JPEG, PNG) */}
                      (Allowed: JPG, JPEG, PNG)
                    </Typography>
                  </Box>
                </label>
              </UploadArea>

              {imagePreview && (
                <Box
                  sx={{ mt: 2, position: "relative", display: "inline-block" }}
                >
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      maxWidth: "200px",
                      maxHeight: "200px",
                      borderRadius: "8px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      objectFit: "contain",
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleRemoveImage}
                    sx={{
                      position: "absolute",
                      top: -10,
                      right: -10,
                      backgroundColor: "white",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                      "&:hover": { backgroundColor: "grey.100" },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {formik.touched.image && formik.errors.image && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="error">
                    {formik.errors.image}
                  </Typography>
                </Box>
              )}
            </Box>

            <div className="mb-3 mt-3">
              <label className="form-label">Banner Link (optional)</label>
              <input
                name="link"
                type="text"
                className={`form-control ${
                  formik.touched.link && formik.errors.link ? "is-invalid" : ""
                }`}
                value={formik.values.link}
                onChange={(e) =>
                  formik.setFieldValue("link", e.target.value?.trim())
                }
                placeholder="https://example.com"
              />
              {formik.touched.link && formik.errors.link && (
                <div className="invalid-feedback">{formik.errors.link}</div>
              )}
            </div>

            <div className="text-end">
              <Button
                variant="secondary"
                onClick={() => setShowModal(false)}
                className="me-2"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                type="submit"
                disabled={formik.isSubmitting}
              >
                Save
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <Modal
        show={showDeleteModal}
        centered
        onHide={() => setShowDeleteModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="m-0">Are you sure you'd like to delete this banner?</p>
        </Modal.Body>
        <Modal.Footer className="text-end gap-1 border-0 px-3 py-2">
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={deleteBanner} disabled={loading}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Banners;
